export const endpoints = {
  auth: {
    login: "/api/auth/login",
    forgotPassword: "/api/auth/forgot-password",
    resetPassword: "/api/auth/reset-password",
    refresh: "/api/auth/refresh",
  },
  employees: {
    list: "/api/employees",
    byId: (id) => `/api/employees/${id}`,
    myProfile: "/api/employees/logineduser/myprofile",
    managers: "/api/employees/all/managers",
    admins: "/api/employees/all/admin",
    directReports: (managerId) => `/api/employees/employees/${managerId}`,
    archive: (id) => `/api/employees/${id}/archive`,
    resetPassword: "/api/employees/myprofile/reset-password",
    remainingLeaves: "/api/employees/leaves/remainingleaves",
    leaveBalance: (id) => `/api/employees/${id}/leavebalance`,
  },
  attendance: {
    checkIn: "/api/attendance/checkin",
    checkOut: "/api/attendance/checkout",
    records: "/api/attendance/attendancerecords",
    byEmployee: (id) => `/api/attendance/employee/${id}`,
    managerApprovals: "/api/attendance/manager-approvals",
    approve: (id) => `/api/attendance/manager-approvals/${id}/approve`,
    reject: (id) => `/api/attendance/manager-approvals/${id}/reject`,
    clientIp: "/api/attendance/client-ip",
    canCheckinFromOffice: "/api/attendance/can-checkin-from-office",
    adminTeamReport: "/api/attendance/admin-team-report",
  },
  leave: {
    types: "/api/leave/types",
    ensureLeaveTypeDefaults: "/api/leave/types/ensure-defaults",
    typeById: (id) => `/api/leave/types/${id}`,
    patchTypeEncashment: (id) => `/api/leave/types/${id}/encashment`,
    requests: "/api/leave/requests",
    previewLeaveDays: "/api/leave/requests/preview-days",
    requestById: (id) => `/api/leave/requests/${id}`,
    getLeaveRequest: "/api/leave/getleaverequest",
    managerRequests: "/api/leave/getmanagerleaverequest",
    updateStatus: (id) => `/api/leave/updateLeaveStatus/${id}`,
    pending: "/api/leave/pending",
    // Advanced-leave endpoints used by LeaveAdvanced.jsx. Previously lived in a
    // second `leave: {...}` object later in this file — object-literal
    // duplicate-key silently shadowed the block above, nuking managerRequests
    // and breaking /leave/approvals UI. Merged here.
    encashment: "/api/leave/encashment",
    encashmentEligibility: "/api/leave/encashment/eligibility",
    approveEncashment: (id) => `/api/leave/encashment/${id}/approve`,
    compOffGrant: "/api/leave/comp-off/grant",
    compOffBalance: "/api/leave/comp-off/balance",
    leaveInLieu: "/api/leave/leave-in-lieu",
    teamBalances: "/api/leave/team-balances",
    teamBalanceById: (employeeid) => `/api/leave/team-balances/${employeeid}`,
  },
  reimbursement: {
    create: "/api/reimbursement/create",
    list: "/api/reimbursement",
    pending: "/api/reimbursement/pending",
    byId: (id) => `/api/reimbursement/${id}`,
    update: (id) => `/api/reimbursement/${id}`,
    remove: (id) => `/api/reimbursement/${id}`,
    updateStatus: "/api/reimbursement/update-expense-status",
    approveExpense: (id, expenseId) => `/api/reimbursement/${id}/expenses/${expenseId}/approve`,
    rejectExpense: (id, expenseId) => `/api/reimbursement/${id}/expenses/${expenseId}/reject`,
    myDetail: "/api/reimbursement/logineduser/get-reimbursement-detail",
  },
  travel: {
    create: "/api/travel-expense/travel",
  },
  helpdesk: {
    createTicket: "/api/helpdesk/tickets",
  },
  overtime: {
    preview: "/api/overtime/preview",
    submit: "/api/overtime/requests",
    myRequests: "/api/overtime/my-requests",
    managerRequests: "/api/overtime/manager-requests",
    updateStatus: (id) => `/api/overtime/update-status/${id}`,
    requestById: (id) => `/api/overtime/requests/${id}`,
  },
  attendanceRegularization: {
    request: "/api/attendance-regularization/request",
  },
  statutory: {
    pfECR: (month) => `/api/statutory/pf-ecr?month=${month}`,
    esi: (month) => `/api/statutory/esi?month=${month}`,
    tds24Q: (fy, q) => `/api/statutory/24q?financialYear=${fy}&quarter=${q}`,
    ptRegister: (month, state) => `/api/statutory/pt-register?month=${month}${state ? `&state=${state}` : ""}`,
    lwfRegister: (month, state) => `/api/statutory/lwf-register?month=${month}${state ? `&state=${state}` : ""}`,
    payrollRegister: (month) => `/api/statutory/payroll-register?month=${month}`,
  },
  timesheet: {
    list: "/api/timesheet",
    mine: "/api/timesheet/logineduser",
    subordinates: "/api/timesheet/logined/getSubordinateTimesheets",
    fromChat: "/api/timesheet/from-chat",
    updateTask: (tsId, taskId) => `/api/timesheet/${tsId}/tasks/${taskId}`,
    deleteTask: (tsId, taskId) => `/api/timesheet/delete/${tsId}/task/${taskId}`,
    // Approval endpoints — previously lived in a duplicate `timesheet` block
    // later in this file that shadowed this one. Merged here.
    approve: (id) => `/api/timesheet/${id}/approve`,
    reject: (id) => `/api/timesheet/${id}/reject`,
    resubmit: (id) => `/api/timesheet/${id}/resubmit`,
    weeklyTotals: "/api/timesheet/weekly-totals",
  },
  holidays: {
    list: "/api/holidays",
    add: "/api/holidays/holidays",
    byId: (id) => `/api/holidays/${id}`,
    tasks: "/api/holidays/tasks",
    events: "/api/holidays/events",
    myEvents: "/api/holidays/logineduser/events",
  },
  inventory: {
    list: "/api/inventory",
    office: "/api/inventory/office-inventory",
    assign: "/api/inventory/assign-inventory",
    byItemId: (id) => `/api/inventory/${id}`,
    byEmployee: (id) => `/api/inventory/employee/${id}`,
    mine: "/api/inventory/getmyinventories",
    unassigned: "/api/inventory/unassigned",
  },
  payroll: {
    config: "/api/payroll/config",
    loans: "/api/payroll/loans",
    loanById: (id) => `/api/payroll/loans/${id}`,
    calculate: "/api/payroll/calculate",
    generatePayslip: "/api/payroll/payslips/generate",
    myPayslips: "/api/payroll/payslips/mine",
    generateMyPayslip: "/api/payroll/payslips/mine/generate",
    payslipById: (id) => `/api/payroll/payslips/${id}`,
    payslipHtml: (id) => `/api/payroll/payslips/${id}/html`,
    payslipPdf: (id) => `/api/payroll/payslips/${id}/pdf`,
    form16: (employeeId, year) => `/api/payroll/form16/${employeeId}/${year}`,
    form16Html: (employeeId, year) => `/api/payroll/form16/${employeeId}/${year}/html`,
    batchRun: "/api/payroll/payslips/batch",
    neftAdvice: "/api/payroll/neft-advice",
    declarationsMine: "/api/payroll/declarations/mine",
    declarationsSubmit: "/api/payroll/declarations/submit",
    declarations: "/api/payroll/declarations",
    verifyDeclaration: (id) => `/api/payroll/declarations/${id}/verify`,
    fbpMine: "/api/payroll/fbp/mine",
    fbpAll: "/api/payroll/fbp",
    fbpTeam: "/api/payroll/fbp/team",
    verifyFBP: (id) => `/api/payroll/fbp/${id}/verify`,
  },
  onboarding: {
    list: "/api/onboarding",
    create: "/api/onboarding",
    byId: (id) => `/api/onboarding/${id}`,
    updateTask: (id, taskId) => `/api/onboarding/${id}/tasks/${taskId}`,
    complete: (id) => `/api/onboarding/${id}/complete`,
  },
  offboarding: {
    list: "/api/offboarding",
    create: "/api/offboarding",
    byId: (id) => `/api/offboarding/${id}`,
    updateClearance: (id, itemId) => `/api/offboarding/${id}/clearance/${itemId}`,
    exitInterview: (id) => `/api/offboarding/${id}/exit-interview`,
    fnf: (id) => `/api/offboarding/${id}/fnf`,
  },
  reports: {
    attendanceSummary: "/api/reports/attendance-summary",
    leaveUtilization: "/api/reports/leave-utilization",
    headcount: "/api/reports/headcount",
    attrition: "/api/reports/attrition",
    payrollSummary: "/api/reports/payroll-summary",
    expensesByCategory: "/api/reports/expenses-by-category",
    // Scheduled-report endpoints — previously in a duplicate `reports` block
    // that shadowed this one. Merged here.
    schedules: "/api/reports/schedules",
    scheduleById: (id) => `/api/reports/schedules/${id}`,
    runScheduleNow: (id) => `/api/reports/schedules/${id}/run-now`,
    exportReport: (type) => `/api/reports/${type}/export`,
  },
  recruitment: {
    jobs: "/api/recruitment/jobs",
    jobById: (id) => `/api/recruitment/jobs/${id}`,
    jobAccess: (id) => `/api/recruitment/jobs/${id}/access`,
    candidates: "/api/recruitment/candidates",
    candidateById: (id) => `/api/recruitment/candidates/${id}`,
    candidatePanel: (id) => `/api/recruitment/candidates/${id}/panelists`,
    interviews: (id) => `/api/recruitment/candidates/${id}/interviews`,
    offer: (candidateId) => `/api/recruitment/candidates/${candidateId}/offer`,
    orgHierarchy: (employeeId) => `/api/recruitment/org/${employeeId}`,
  },
  training: {
    courses: "/api/training/courses",
    courseById: (id) => `/api/training/courses/${id}`,
    enrollments: "/api/training/enrollments",
    certifications: "/api/training/certifications",
    // Advanced training endpoints — merged from duplicate `training` block that
    // shadowed this one.
    quizAttempt: (id) => `/api/training/enrollments/${id}/quiz-attempt`,
    learningPaths: "/api/training/learning-paths",
    enrollInPath: (id) => `/api/training/learning-paths/${id}/enroll`,
    overdue: "/api/training/overdue",
  },
  policies: {
    list: "/api/policies",
    create: "/api/policies",
    byId: (id) => `/api/policies/${id}`,
    acknowledge: (id) => `/api/policies/${id}/acknowledge`,
    unpublish: (id) => `/api/policies/${id}/unpublish`,
  },
  announcements: {
    list: "/api/announcements",
    byId: (id) => `/api/announcements/${id}`,
  },
  goals: {
    list: "/api/goals",
    byId: (id) => `/api/goals/${id}`,
    updateProgress: (id) => `/api/goals/${id}/progress`,
  },
  notifications: {
    fetch: "/api/notification/fetch",
    send: "/api/notification/send",
    dismiss: "/api/notification/dismiss",
    preferences: "/api/notification/preferences",
    snooze: "/api/notification/snooze",
  },
  selfService: {
    me: "/api/self-service/me",
    profilePicture: "/api/self-service/me/profile-picture",
    directory: "/api/self-service/directory",
    access: "/api/self-service/access",
    orgChart: "/api/self-service/org-chart",
    calendarEvents: "/api/self-service/calendar-events",
    documents: "/api/self-service/documents",
    documentById: (id) => `/api/self-service/documents/${id}`,
    documentsPendingVerification: "/api/self-service/documents/pending-verification",
    documentsExpiring: "/api/self-service/documents/expiring",
    verifyDocument: (id) => `/api/self-service/documents/${id}/verify`,
    letterTemplates: "/api/self-service/letters/templates",
    previewLetter: "/api/self-service/letters/preview",
    issueLetter: "/api/self-service/letters/issue",
    letters: "/api/self-service/letters",
    letterById: (id) => `/api/self-service/letters/${id}`,
  },
  performance: {
    reviews: "/api/performance/reviews/mine",
    reviewById: (id) => `/api/performance/reviews/${id}`,
    peerNominate: "/api/performance/reviews/peer-nominate",
    peerNominationApprove: (id) => `/api/performance/reviews/peer-nominate/${id}/approve`,
    acknowledgeReview: (id) => `/api/performance/reviews/${id}/acknowledge`,
    managerSignReview: (id) => `/api/performance/reviews/${id}/manager-sign`,
    overrideNineBox: (id) => `/api/performance/reviews/${id}/override`,
    assignTalentPool: (id) => `/api/performance/reviews/${id}/talent-pool`,
    distributionCheck: (cycleId) => `/api/performance/cycles/${cycleId}/distribution-check`,
    pips: "/api/performance/pips",
    pip: (id) => `/api/performance/pips/${id}`,
    pipMidPoint: (id) => `/api/performance/pips/${id}/mid-point-review`,
    pipCriteria: (id) => `/api/performance/pips/${id}/criteria`,
    pipAttachment: (id) => `/api/performance/pips/${id}/attachments`,
    pipHRSignOff: (id) => `/api/performance/pips/${id}/hr-sign-off`,
    pipConfig: "/api/performance/pip-config",
    pipTemplates: "/api/performance/pip-templates",
    pipTemplate: (id) => `/api/performance/pip-templates/${id}`,
    pipAnalytics: "/api/performance/pips/analytics",
    pipExport: "/api/performance/pips/export",
    pipApprove: (id) => `/api/performance/pips/${id}/approve`,
    pipReject: (id) => `/api/performance/pips/${id}/reject`,
    pipMeetings: (id) => `/api/performance/pips/${id}/meetings`,
    pipMeeting: (id, mid) => `/api/performance/pips/${id}/meetings/${mid}`,
    pipProgress: (id) => `/api/performance/pips/${id}/progress`,
    pipEvidence: (id) => `/api/performance/pips/${id}/evidence`,
    pipComments: (id) => `/api/performance/pips/${id}/comments`,
    pipClarifications: (id) => `/api/performance/pips/${id}/clarifications`,
    pipClarificationAnswer: (id, cid) => `/api/performance/pips/${id}/clarifications/${cid}/answer`,
    pipRecommend: (id) => `/api/performance/pips/${id}/recommend`,
    kudos: "/api/performance/kudos",
    kudosLeaderboard: "/api/performance/kudos/leaderboard",
    kudosMyPoints: "/api/performance/kudos/my-points",
    kudosRedeem: (id) => `/api/performance/kudos/${id}/redeem`,
  },
  // NOTE: duplicate `training`, `leave`, `timesheet` blocks that used to live
  // here were shadowing the canonical definitions above and silently dropping
  // keys like leave.managerRequests. Their unique keys have been merged into
  // the canonical blocks; this spot is intentionally empty.
  security: {
    regenerateBackupCodes: "/api/security/2fa/backup-codes/regenerate",
    setRecovery: "/api/security/2fa/recovery",
    revokeOthers: "/api/security/sessions/revoke-others",
    exportAuditLog: "/api/security/audit-log/export",
    setAuditRetention: "/api/security/audit-log/retention",
  },
  // Duplicate `reports: { ... }` block removed — its keys (schedules,
  // scheduleById, runScheduleNow, exportReport) are now merged into the
  // canonical reports block earlier in this file.
  onboardingExt: {
    templates: "/api/onboarding/templates",
    templateById: (id) => `/api/onboarding/templates/${id}`,
    preJoiningLink: (id) => `/api/onboarding/${id}/pre-joining-link`,
    publicPreJoining: (token) => `/api/public/pre-joining/${token}`,
    submitPreJoining: (token) => `/api/public/pre-joining/${token}/submit`,
  },
  payrollExt: {
    addArrears: (id) => `/api/payroll/runs/${id}/arrears`,
    lockRun: (id) => `/api/payroll/runs/${id}/lock`,
    amendRun: (id) => `/api/payroll/runs/${id}/amend`,
    holdPayment: (id) => `/api/payroll/runs/${id}/hold`,
    sendPayslip: (id) => `/api/payroll/payslips/${id}/send`,
    loanAmortization: (id) => `/api/payroll/loans/${id}/amortization`,
    forecloseLoan: (id) => `/api/payroll/loans/${id}/foreclose`,
    topUpLoan: (id) => `/api/payroll/loans/${id}/top-up`,
    hraExemption: "/api/payroll/hra-exemption",
    gratuityCalc: (employeeId) => `/api/payroll/gratuity/${employeeId}`,
  },
  statutoryExt: {
    form26Q: "/api/statutory/26q",
    form16PartA: (employeeId) => `/api/statutory/16-part-a/${employeeId}`,
    bankAdvice: "/api/statutory/bank-advice",
    gratuityLiability: "/api/statutory/gratuity-liability",
  },
  recruitmentExt: {
    requestJobApproval: (id) => `/api/recruitment/jobs/${id}/request-approval`,
    approveJobStep: (id) => `/api/recruitment/jobs/${id}/approve`,
    publishJob: (id) => `/api/recruitment/jobs/${id}/publish`,
    jobTemplates: "/api/recruitment/job-templates",
    jobTemplateById: (id) => `/api/recruitment/job-templates/${id}`,
    requestOfferApproval: (id) => `/api/recruitment/candidates/${id}/offer/request-approval`,
    approveOfferStep: (id) => `/api/recruitment/candidates/${id}/offer/approve`,
    counterOffer: (id) => `/api/recruitment/candidates/${id}/offer/counter`,
    respondCounterOffer: (id) => `/api/recruitment/candidates/${id}/offer/counter/respond`,
    addToTalentPool: (id) => `/api/recruitment/candidates/${id}/talent-pool`,
    candidateWithdraw: (id) => `/api/recruitment/candidates/${id}/withdraw`,
    talentPool: "/api/recruitment/talent-pool",
    gdprForget: (id) => `/api/recruitment/candidates/${id}/gdpr-delete`,
    rateCandidate: (id) => `/api/recruitment/candidates/${id}/rating`,
    addCommunication: (id) => `/api/recruitment/candidates/${id}/communication`,
    funnelAnalytics: "/api/recruitment/analytics/funnel",
    timeToHire: "/api/recruitment/analytics/time-to-hire",
    interviewerLoad: "/api/recruitment/analytics/interviewer-load",
  },
  helpdeskExt: {
    slaPolicies: "/api/helpdesk/sla-policies",
    canned: "/api/helpdesk/canned",
    mergeTicket: (id) => `/api/helpdesk/tickets/${id}/merge`,
    csat: "/api/helpdesk/csat",
    kbCategories: "/api/helpdesk/kb/categories",
    kbView: (id) => `/api/helpdesk/kb/${id}/view`,
  },
  announcementsExt: {
    comment: (id) => `/api/announcements/${id}/comments`,
    react: (id) => `/api/announcements/${id}/react`,
    ackReport: (id) => `/api/announcements/${id}/ack-report`,
    read: (id) => `/api/announcements/${id}/read`,
  },
  policiesExt: {
    versions: (id) => `/api/policies/${id}/versions`,
    diff: (id) => `/api/policies/${id}/diff`,
    exportAcks: (id) => `/api/policies/${id}/acknowledgments/export`,
  },
  messagesExt: {
    outOfOffice: "/api/mailmessage/out-of-office",
    outOfOfficeHistory: "/api/mailmessage/out-of-office/history",
    outOfOfficeHistoryItem: (id) => `/api/mailmessage/out-of-office/history/${id}`,
    outOfOfficeHistoryStatus: (id) => `/api/mailmessage/out-of-office/history/${id}/status`,
    moveFolder: (id) => `/api/mailmessage/messages/${id}/folder`,
    labels: (id) => `/api/mailmessage/messages/${id}/labels`,
  },
  chatExt: {
    reactions: "/api/chat/reactions",
    edit: (id) => `/api/chat/messages/${id}/edit`,
    deleteMsg: (id) => `/api/chat/messages/${id}`,
    search: "/api/chat/search",
    group: (id) => `/api/chat/groups/${id}`,
    addMembers: (id) => `/api/chat/groups/${id}/members/add`,
    removeMembers: (id) => `/api/chat/groups/${id}/members/remove`,
    markRead: (id) => `/api/chat/messages/${id}/read`,
  },
  attendanceExt: {
    punchIn: "/api/attendance/punch-in",
    punchOut: "/api/attendance/punch-out",
    wfh: "/api/attendance/wfh",
  },
  attendanceAdv: {
    shifts: "/api/attendance-adv/shifts",
    shiftById: (id) => `/api/attendance-adv/shifts/${id}`,
    shiftAssignments: "/api/attendance-adv/shift-assignments",
    myShift: "/api/attendance-adv/my-shift",
    geoFences: "/api/attendance-adv/geo-fences",
    geoFenceById: (id) => `/api/attendance-adv/geo-fences/${id}`,
    validateGeoFence: "/api/attendance-adv/geo-fences/validate",
    officeIps: "/api/attendance-adv/office-ips",
    officeIpById: (id) => `/api/attendance-adv/office-ips/${id}`,
    shiftSwap: "/api/attendance-adv/shift-swap",
    shiftSwapMine: "/api/attendance-adv/shift-swap/mine",
    shiftSwapCounterparty: (id) => `/api/attendance-adv/shift-swap/${id}/counterparty`,
    shiftSwapManager: (id) => `/api/attendance-adv/shift-swap/${id}/manager`,
    teamCalendar: "/api/attendance-adv/team-calendar",
    leaveAccrual: "/api/attendance-adv/leave/accrual-run",
    myLeaveBalance: "/api/attendance-adv/leave/my-balance",
    sandwichCheck: "/api/attendance-adv/leave/sandwich-check",
  },
  pm: {
    assignees: "/api/pm/assignees",
    teams: "/api/pm/teams",
    teamById: (id) => `/api/pm/teams/${id}`,
    teamMembers: (id) => `/api/pm/teams/${id}/members`,
    teamMemberById: (id, eid) => `/api/pm/teams/${id}/members/${eid}`,
    projects: "/api/pm/projects",
    projectById: (id) => `/api/pm/projects/${id}`,
    projectStats: (id) => `/api/pm/projects/${id}/stats`,
    projectMembers: (id) => `/api/pm/projects/${id}/members`,
    projectMemberById: (id, eid) => `/api/pm/projects/${id}/members/${encodeURIComponent(eid)}`,
    projectMilestones: (id) => `/api/pm/projects/${id}/milestones`,
    projectMilestoneById: (id, mid) => `/api/pm/projects/${id}/milestones/${mid}`,
    projectFields: (id) => `/api/pm/projects/${id}/fields`,
    projectFieldById: (id, fid) => `/api/pm/projects/${id}/fields/${fid}`,
    rules: "/api/pm/rules",
    ruleById: (id) => `/api/pm/rules/${id}`,
    sprints: "/api/pm/sprints",
    sprintById: (id) => `/api/pm/sprints/${id}`,
    tasks: "/api/pm/tasks",
    taskById: (id) => `/api/pm/tasks/${id}`,
    moveTask: (id) => `/api/pm/tasks/${id}/move`,
    addComment: (id) => `/api/pm/tasks/${id}/comments`,
    taskWorklogs: (id) => `/api/pm/tasks/${id}/worklogs`,
    taskWorklogById: (id, wid) => `/api/pm/tasks/${id}/worklogs/${wid}`,
    views: "/api/pm/views",
    viewById: (id) => `/api/pm/views/${id}`,
    dependencies: "/api/pm/dependencies",
    dependencyById: (id) => `/api/pm/dependencies/${id}`,
    workload: "/api/pm/workload",
  },
  ai: {
    status: "/api/ai/status",
    chat: "/api/ai/chat",
    chatIntents: "/api/ai/chat/intents",
    ask: "/api/ai/ask",
    knowledgeStatus: "/api/ai/knowledge/status",
    knowledgeReindex: "/api/ai/knowledge/reindex",
    ocrReceipt: "/api/ai/ocr/receipt",
    draftLetter: "/api/ai/draft-letter",
  },
};
